import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { Field } from "./Fleet";
import { Upload } from "lucide-react";
import { validateRut, formatRut } from "@/lib/rut";

export default function Settings() {
  const { user, refreshCompany } = useAuth();
  const [form, setForm] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/companies/me")
      .then((r) => setForm(r.data))
      .catch((e) => {
        if (e.response?.status === 404) setForm({ _no_company: true });
      });
  }, []);

  if (!form) return <div className="p-8">Cargando...</div>;

  if (form._no_company) {
    return (
      <div data-testid="settings-page">
        <PageHeader
          tag="Configuración"
          title="Sin empresa asociada"
          description="Tu rol (SuperAdmin) no pertenece a una empresa específica. Configura el branding desde el panel SuperAdmin o desde una cuenta de empresa."
        />
        <div className="status-warning p-6 max-w-2xl">
          Inicia sesión con una cuenta de Rent-a-Car, Taller o Cliente para configurar logo y datos comerciales.
        </div>
      </div>
    );
  }

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setError("");
    if (form.rut && !validateRut(form.rut)) {
      setError("RUT empresa inválido");
      return;
    }
    try {
      let logo_path = form.logo_path;
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        logo_path = up.data.path;
      }
      const payload = {
        name: form.name,
        rut: form.rut ? formatRut(form.rut) : "",
        address: form.address,
        phone: form.phone,
        email: form.email,
        logo_path,
      };
      await api.put("/companies/me", payload);
      await refreshCompany();
      setMsg("Cambios guardados.");
      setLogoFile(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader
        tag="Configuración"
        title="Branding & Empresa"
        description="Tu logo e información se utilizan en todos los PDFs (Órdenes de Servicio y Certificados)."
      />
      <form onSubmit={submit} className="max-w-3xl space-y-6" data-testid="settings-form">
        <div className="border border-gray-200 p-6">
          <p className="veta-tag mb-4">Logo</p>
          <div className="flex items-center gap-6 flex-wrap">
            <div className="w-32 h-32 border border-gray-200 flex items-center justify-center bg-gray-50">
              {form.logo_path ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files?path=${encodeURIComponent(form.logo_path)}`} alt="logo" className="max-w-full max-h-full" />
              ) : (
                <span className="veta-tag">Sin logo</span>
              )}
            </div>
            <label className="h-touch px-6 border-2 border-black text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white cursor-pointer flex items-center gap-2">
              <Upload size={16} strokeWidth={2.5} />
              {logoFile ? logoFile.name : "Subir Logo"}
              <input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0])} className="hidden" data-testid="logo-input" />
            </label>
          </div>
        </div>

        <div className="border border-gray-200 p-6 space-y-4">
          <p className="veta-tag mb-2">Información comercial</p>
          <Field label="Nombre / Razón Social" required value={form.name || ""} onChange={update("name")} testId="company-name" />
          <Field label="RUT empresa" value={form.rut || ""} onChange={update("rut")} mono placeholder="76.123.456-7" testId="company-rut" />
          <Field label="Dirección" value={form.address || ""} onChange={update("address")} testId="company-address" />
          <Field label="Teléfono" value={form.phone || ""} onChange={update("phone")} mono testId="company-phone" />
          <Field label="Correo" type="email" value={form.email || ""} onChange={update("email")} mono testId="company-email" />
        </div>

        {msg && <div className="status-success p-3 text-sm" data-testid="settings-success">{msg}</div>}
        {error && <div className="status-danger p-3 text-sm" data-testid="settings-error">{error}</div>}

        <button type="submit" className="h-touch px-8 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 active:scale-[0.98]" data-testid="save-settings-btn">
          Guardar cambios
        </button>
      </form>
    </div>
  );
}
